import { useMemo } from "react";
import type { TradeOrder, VybeDealInternal, VybeOrderInternal } from "../types";
import { VybeOrderType, dealOpenTimeMs } from "../types";
import { useSortStore } from "../stores/useSortStore";

// ============================================
// POSITIONS SORTING
// ============================================

/**
 * Hook for sorting positions
 * @param trades - array of TradeOrder to sort
 * @returns sorted array of TradeOrder
 */
export function useSortPositions(trades: TradeOrder[]): TradeOrder[] {
  const positionsSort = useSortStore((state) => state.positionsSort);

  return useMemo(() => {
    // If no sorting or set to 'default', return original data
    if (!positionsSort || positionsSort.field === "default") {
      return trades;
    }

    // Create a copy of array for sorting
    const sortedTrades = [...trades];

    sortedTrades.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      // Map sort fields to TradeOrder properties
      switch (positionsSort.field) {
        case "symbol":
          aValue = a.symbol;
          bValue = b.symbol;
          break;

        case "ticket":
          aValue = a.ticket;
          bValue = b.ticket;
          break;

        case "type":
          aValue = a.orderType;
          bValue = b.orderType;
          break;

        case "volume":
          aValue = a.lots;
          bValue = b.lots;
          break;

        case "openTime":
          aValue = new Date(a.openTime).getTime();
          bValue = new Date(b.openTime).getTime();
          break;

        case "closeTime":
          aValue = new Date(a.closeTime).getTime();
          bValue = new Date(b.closeTime).getTime();
          break;

        case "profit":
          aValue = a.profit;
          bValue = b.profit;
          break;

        default:
          return 0;
      }

      // Handle null/undefined
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      // String sorting
      if (typeof aValue === "string" && typeof bValue === "string") {
        return positionsSort.direction === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      // Number sorting
      if (typeof aValue === "number" && typeof bValue === "number") {
        return positionsSort.direction === "asc"
          ? aValue - bValue
          : bValue - aValue;
      }

      return 0;
    });

    return sortedTrades;
  }, [trades, positionsSort]);
}

// ============================================
// ORDERS SORTING
// ============================================

// Extended type for deal with additional state

/**
 * Hook for sorting orders
 * @param orders - array of VybeOrderInternal to sort
 * @returns sorted array of VybeOrderInternal
 */
export function useSortOrders(
  orders: VybeOrderInternal[]
): VybeOrderInternal[] {
  const ordersSort = useSortStore((state) => state.ordersSort);

  return useMemo(() => {
    // If no sorting or set to 'default', return original data
    if (!ordersSort || ordersSort.field === "default") {
      return orders;
    }

    // Create a copy of array for sorting
    const sortedOrders = [...orders];

    sortedOrders.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      // Map sort fields to order properties
      switch (ordersSort.field) {
        case "symbol":
          aValue = a.symbol;
          bValue = b.symbol;
          break;

        case "order":
          // For 'order' field just return 0 to keep default order
          return 0;

        case "type":
          aValue = a.type;
          bValue = b.type;
          break;

        case "volume":
          aValue = a.lots;
          bValue = b.lots;
          break;

        case "openTime":
          aValue = a.openTimeMs;
          bValue = b.openTimeMs;
          break;

        case "closeTime":
          aValue = a.openTimeMs;
          bValue = b.openTimeMs;
          break;

        case "state":
          aValue = a.state;
          bValue = b.state;
          break;

        default:
          return 0;
      }

      // Handle null/undefined
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      // String sorting
      if (typeof aValue === "string" && typeof bValue === "string") {
        return ordersSort.direction === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      // Number sorting
      if (typeof aValue === "number" && typeof bValue === "number") {
        return ordersSort.direction === "asc"
          ? aValue - bValue
          : bValue - aValue;
      }

      return 0;
    });

    return sortedOrders;
  }, [orders, ordersSort]);
}

// ============================================
// DEALS SORTING
// ============================================

/**
 * Hook for sorting deals
 * @param deals - array of VybeDealInternal to sort
 * @returns sorted array of VybeDealInternal
 */
export function useSortDeals(deals: VybeDealInternal[]): VybeDealInternal[] {
  const dealsSort = useSortStore((state) => state.dealsSort);

  return useMemo(() => {
    // If no sorting or set to 'default', return original data
    if (!dealsSort || dealsSort.field === "default") {
      return deals;
    }

    // Create a copy of array for sorting
    const sortedDeals = [...deals];

    sortedDeals.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      // Map sort fields to deal properties
      switch (dealsSort.field) {
        case "symbol":
          aValue = a.symbol;
          bValue = b.symbol;
          break;

        case "deal":
          // For 'deal' field just return 0 to keep default order
          return 0;

        case "position":
          aValue = a.positionTicket;
          bValue = b.positionTicket;
          break;

        case "type":
          aValue = a.type;
          bValue = b.type;
          break;

        case "volume":
          aValue = a.lots;
          bValue = b.lots;
          break;

        case "time":
          aValue = dealOpenTimeMs(a);
          bValue = dealOpenTimeMs(b);
          break;

        case "profit":
          aValue = a.profit;
          bValue = b.profit;
          break;

        default:
          return 0;
      }

      // Handle null/undefined
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      // String sorting
      if (typeof aValue === "string" && typeof bValue === "string") {
        return dealsSort.direction === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      // Number sorting
      if (typeof aValue === "number" && typeof bValue === "number") {
        return dealsSort.direction === "asc"
          ? aValue - bValue
          : bValue - aValue;
      }

      return 0;
    });

    return sortedDeals;
  }, [deals, dealsSort]);
}
